import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const MAX_LINKED_ACCOUNTS = 2;

const normalizeCodes = (codes) =>
    (codes || [])
        .map((code) => `${code}`.trim())
        .filter(Boolean);

const uniqueCodes = (codes) => Array.from(new Set(normalizeCodes(codes)));

const buildPrimary = (accounts, primary) => {
    if (accounts.length === 0) return "";
    if (primary && accounts.includes(primary)) return primary;
    return accounts[0];
};

const normalizeSet = (set) => {
    const accounts = uniqueCodes(set.accounts || []);
    return {
        accounts,
        primary_customer_code: buildPrimary(accounts, set.primary_customer_code || ""),
    };
};

const WarehousePreferences = ({ customerCode, currentUser, editUser, onClose, readOnly = false }) => {
    const [loading, setLoading] = useState(true);
    const [availableAccounts, setAvailableAccounts] = useState([]);
    const [linkSets, setLinkSets] = useState([]);
    const [accountMeta, setAccountMeta] = useState({});
    const [warnings, setWarnings] = useState([]);
    const [filterBySet, setFilterBySet] = useState({});

    useEffect(() => {
        loadData();
    }, [editUser?.id]);

    const assignedSetByCode = useMemo(() => {
        const map = {};
        linkSets.forEach((set, setIndex) => {
            set.accounts.forEach((code) => {
                map[code] = setIndex;
            });
        });
        return map;
    }, [linkSets]);

    const loadData = async () => {
        if (!currentUser) return;
        setLoading(true);

        const setupCustomerCode = `${customerCode || editUser?.customer_code || ""}`.trim();
        if (!setupCustomerCode) {
            setAvailableAccounts([]);
            setLinkSets([]);
            setAccountMeta({});
            setWarnings([]);
            setLoading(false);
            return;
        }

        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_API_URL}customer/${encodeURIComponent(setupCustomerCode)}/warehouse-preferences/setup`,
                {
                    headers: { Authorization: `Bearer ${currentUser.token}` },
                    params: { user_id: editUser?.id },
                }
            );

            const accountsFromServer = uniqueCodes(
                data.customer_codes || (editUser?.customer_code ? editUser.customer_code.split(",") : [])
            );
            const linkedAccounts = uniqueCodes(data.linked_accounts || []);
            const primaryFromServer = data.primary_customer_code ? `${data.primary_customer_code}`.trim() : "";
            const setsFromServer = Array.isArray(data.link_sets) ? data.link_sets.map(normalizeSet) : null;

            const productGroups = Array.isArray(data.product_groups) ? data.product_groups : [];
            const warehouses = Array.isArray(data.warehouses) ? data.warehouses : [];
            const groupsByFlags = data.customer_product_groups_flags || {};
            const groupsByTable = data.customer_product_groups_table || {};
            const warehousesByCustomer = data.customer_warehouses || {};

            const groupLabelByCode = new Map(
                productGroups.map((group) => [`${group.code}`.trim(), group.label || group.code])
            );
            const warehouseLabelByCode = new Map(
                warehouses.map((warehouse) => [`${warehouse.code}`.trim(), warehouse.label || warehouse.code])
            );

            const uniqueAccounts = Array.from(new Set([...accountsFromServer, ...linkedAccounts]));
            const meta = {};
            uniqueAccounts.forEach((code) => {
                const normalizedCode = `${code}`.trim();
                const groups = [
                    ...(groupsByFlags[normalizedCode] || []),
                    ...(groupsByTable[normalizedCode] || []),
                ]
                    .map((group) => `${group}`.trim())
                    .filter(Boolean);
                const productGroupLabels = Array.from(new Set(groups)).map(
                    (group) => groupLabelByCode.get(group) || group
                );

                const warehouseInfo = warehousesByCustomer[normalizedCode] || {};
                const salesWarehouse = warehouseInfo.sales_warehouse
                    ? `${warehouseInfo.sales_warehouse}`.trim()
                    : "";
                const salesWarehouseLabel = salesWarehouse
                    ? warehouseLabelByCode.get(salesWarehouse) || salesWarehouse
                    : "";

                meta[normalizedCode] = {
                    productGroups: productGroupLabels,
                    salesWarehouse: salesWarehouseLabel || salesWarehouse,
                    branch: warehouseInfo.branch ? `${warehouseInfo.branch}`.trim() : "",
                };
            });

            let normalizedSets = [];
            if (setsFromServer && setsFromServer.length > 0) {
                normalizedSets = setsFromServer;
            } else if (linkedAccounts.length > 0) {
                normalizedSets = [
                    normalizeSet({
                        accounts: linkedAccounts,
                        primary_customer_code: primaryFromServer,
                    }),
                ];
            }

            setAvailableAccounts(uniqueAccounts);
            setLinkSets(normalizedSets);
            setAccountMeta(meta);
            setWarnings(Array.isArray(data.warnings) ? data.warnings : []);
        } catch (err) {
            console.error(err);
        }

        setLoading(false);
    };

    const updateSet = (setIndex, updater) => {
        setLinkSets((prev) => prev.map((set, index) => (index === setIndex ? updater(set) : set)));
    };

    const toggleAccount = (setIndex, code) => {
        if (readOnly) return;

        const assignedIndex = assignedSetByCode[code];
        if (assignedIndex !== undefined && assignedIndex !== setIndex) return;

        updateSet(setIndex, (set) => {
            if (set.accounts.includes(code)) {
                const accounts = set.accounts.filter((acc) => acc !== code);
                return {
                    ...set,
                    accounts,
                    primary_customer_code: buildPrimary(accounts, set.primary_customer_code),
                };
            }

            if (set.accounts.length >= MAX_LINKED_ACCOUNTS) {
                alert("Only 2 customer codes can be linked in a set.");
                return set;
            }

            const accounts = [...set.accounts, code];
            return {
                ...set,
                accounts,
                primary_customer_code: buildPrimary(accounts, set.primary_customer_code),
            };
        });
    };

    const addSet = () => {
        if (readOnly) return;
        setLinkSets((prev) => [...prev, { accounts: [], primary_customer_code: "" }]);
    };

    const removeSet = (setIndex) => {
        if (readOnly) return;
        setLinkSets((prev) => prev.filter((_, index) => index !== setIndex));
        setFilterBySet((prev) => {
            const next = { ...prev };
            delete next[setIndex];
            return next;
        });
    };

    const handleSaveLinks = async () => {
        const setupCustomerCode = `${customerCode || editUser?.customer_code || ""}`.trim();

        const setsToSave = linkSets
            .map((set) => normalizeSet(set))
            .filter((set) => set.accounts.length > 0);

        for (let i = 0; i < setsToSave.length; i += 1) {
            const set = setsToSave[i];
            if (set.accounts.length > MAX_LINKED_ACCOUNTS) {
                alert(`Only 2 customer codes can be linked in set ${i + 1}.`);
                return;
            }
            if (!set.primary_customer_code || !set.accounts.includes(set.primary_customer_code)) {
                alert(`Select a primary customer code for set ${i + 1}.`);
                return;
            }
        }

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}customer/${encodeURIComponent(setupCustomerCode)}/link-accounts`,
                {
                    link_sets: setsToSave,
                    user_id: editUser?.id,
                },
                { headers: { Authorization: `Bearer ${currentUser.token}` } }
            );
            if (res.data.success) {
                alert("Linked accounts saved");
                loadData();
            } else {
                alert("Save failed");
            }
        } catch (err) {
            console.error(err);
            alert("Failed to save links: " + (err?.response?.data?.message || err.message));
        }
    };

    if (loading) return <div>Loading...</div>;

    const getMeta = (code) =>
        accountMeta[code] || {
            productGroups: [],
            salesWarehouse: "",
            branch: "",
        };

    const getWarehouseLabel = (meta) => {
        const parts = [];
        if (meta.salesWarehouse) parts.push(meta.salesWarehouse);
        if (meta.branch) parts.push(`Branch ${meta.branch}`);
        return parts.length > 0 ? parts.join(" - ") : "--";
    };

    return (
        <div>
            <h2 className="text-xl font-bold mb-4">Linked Accounts</h2>

            <p className="text-sm text-gray-600 mb-4">
                Each set can contain up to two customer codes. One must be selected as the primary account.
            </p>

            {warnings.length > 0 && (
                <div className="mb-4 rounded border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
                    {warnings.map((warning, index) => (
                        <div key={`${warning}-${index}`}>{warning}</div>
                    ))}
                </div>
            )}

            {linkSets.length === 0 && (
                <div className="mb-6 text-sm text-gray-600">
                    No linked sets yet. Click "Add set" to create the first one.
                </div>
            )}

            {linkSets.map((set, setIndex) => (
                <div key={`set-${setIndex}`} className="mb-8">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold mb-2">Linked set {setIndex + 1}</h3>
                        {!readOnly && (
                            <button
                                type="button"
                                className="text-sm text-red-600"
                                onClick={() => removeSet(setIndex)}
                            >
                                Remove set
                            </button>
                        )}
                    </div>

                    <div className="mb-4">
                        <div className="mb-2">
                            <input
                                type="text"
                                placeholder="Filter customer codes..."
                                className="w-full max-w-xs rounded border px-3 py-2 text-sm"
                                value={filterBySet[setIndex] || ""}
                                onChange={(event) =>
                                    setFilterBySet((prev) => ({
                                        ...prev,
                                        [setIndex]: event.target.value,
                                    }))
                                }
                                disabled={readOnly}
                            />
                        </div>
                        <div className="flex flex-wrap gap-4">
                            {availableAccounts
                                .filter((code) => {
                                    const assignedIndex = assignedSetByCode[code];
                                    if (assignedIndex !== undefined && assignedIndex !== setIndex) {
                                        return false;
                                    }
                                    const query = `${filterBySet[setIndex] || ""}`.trim().toLowerCase();
                                    if (!query) return true;
                                    if (set.accounts.includes(code)) return true;
                                    return `${code}`.toLowerCase().includes(query);
                                })
                                .map((code) => {
                                const assignedIndex = assignedSetByCode[code];
                                const isChecked = set.accounts.includes(code);
                                const isDisabled =
                                    readOnly ||
                                    (assignedIndex !== undefined && assignedIndex !== setIndex);

                                return (
                                    <label key={`${code}-${setIndex}`} className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            disabled={isDisabled}
                                            onChange={() => toggleAccount(setIndex, code)}
                                        />
                                        {code}
                                    </label>
                                );
                                })}
                        </div>
                    </div>

                    <table className="min-w-full table-fixed">
                        <colgroup>
                            <col style={{ width: "15%" }} />
                            <col style={{ width: "40%" }} />
                            <col style={{ width: "15%" }} />
                            <col style={{ width: "15%" }} />
                            <col style={{ width: "15%" }} />
                        </colgroup>
                        <thead>
                            <tr className="text-left">
                                <th className="px-2 py-2">Customer Code</th>
                                <th className="px-2 py-2">Product Groups</th>
                                <th className="px-2 py-2">Warehouses</th>
                                <th className="px-2 py-2">Primary</th>
                                <th className="px-2 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {set.accounts.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-2 py-4 text-left">
                                        No accounts selected in this set
                                    </td>
                                </tr>
                            )}
                            {set.accounts.map((code, rowIndex) => {
                                const meta = getMeta(code);
                                const warehouseLabel = getWarehouseLabel(meta);
                                const rowClass = rowIndex % 2 === 0 ? "bg-gray-50" : "bg-white";
                                return (
                                    <tr key={`${code}-${setIndex}`} className={`border-t ${rowClass}`}>
                                        <td className="px-2 py-2 text-left">{code}</td>
                                        <td className="px-2 py-2 text-left whitespace-normal break-words">
                                            {meta.productGroups.length > 0 ? meta.productGroups.join(", ") : "--"}
                                        </td>
                                        <td className="px-2 py-2 text-left whitespace-normal break-words">
                                            {warehouseLabel}
                                        </td>
                                        <td className="px-2 py-2 text-left">
                                            <input
                                                type="radio"
                                                name={`primaryAccount-${setIndex}`}
                                                checked={set.primary_customer_code === code}
                                                onChange={() =>
                                                    updateSet(setIndex, (prev) => ({
                                                        ...prev,
                                                        primary_customer_code: code,
                                                    }))
                                                }
                                                disabled={readOnly}
                                            />
                                        </td>
                                        <td className="px-2 py-2 text-left">
                                            <button
                                                type="button"
                                                className="px-2 py-1 border rounded"
                                                onClick={() => toggleAccount(setIndex, code)}
                                                disabled={readOnly}
                                            >
                                                Remove
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ))}

            {!readOnly && (
                <button
                    type="button"
                    className="mb-6 px-4 py-2 border rounded"
                    onClick={addSet}
                >
                    Add set
                </button>
            )}

            <div className="mt-6 flex justify-end gap-4">
                <button className="px-4 py-2 bg-gray-200 rounded" onClick={onClose}>
                    Cancel
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded" onClick={handleSaveLinks}>
                    Save Linked Accounts
                </button>
            </div>
        </div>
    );
};

export default WarehousePreferences;
